import { html } from "lit-html";
import { HomeWidgetValue, LocalizedWidget } from "./AdminPageViewModel";
import { submitActionOnlyPost } from "./adminCommon";
import { useAjax } from "Services/useAjax";
import classNames from "classnames";

export interface Props {
	homeWidgetsValues: HomeWidgetValue[];
	isPreview: boolean;
	state: string;
}

const PROHIBITED_WIDGETS = ["variables", "services"];

export const useWidgetsSection = (props: Props) => {
	const { ajaxRequest } = useAjax();

	const invalidate = async (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		await submitActionOnlyPost("invalidate_homewidgets", ajaxRequest);
	};

	const stateTemplate = () => html`
		<ul class="list-none text-xs text-n-dark-gray">
			<li><span class="font-bold uppercase">Cache State:</span> ${props.state}</li>
		</ul>
	`;

	const buttonTemplate = () => html`
		<button type="submit" class="bg-n-blue hover:bg-n-red hover:text-white" value="Invalidate" @click=${invalidate}>
			Invalidate
		</button>
	`;

	const localizedWidgetTemplate = (localizedWidget: LocalizedWidget) => {
		const validityIconTemplate = localizedWidget.IsValid ? html`✔` : html`✘`;
		const errorMessageTemplate = !localizedWidget.IsValid
			? html`<label class="text-xs text-[#db0000]">Message: ${localizedWidget.LastRefreshMessage}</label>`
			: "";

		return html`
		<li
			class=${classNames("flex flex-col gap-2", {
				"text-[#db0000]": !localizedWidget.IsValid,
				"text-[#83b245]": localizedWidget.IsValid,
			})}
		>
			<span class="text-xs">
				<span> ${localizedWidget.Locale} </span>
				<span> ${validityIconTemplate} </span>
			</span>
			${errorMessageTemplate}
		</li>
	`;
	};

	const widgetTemplate = (widget: HomeWidgetValue) => {
		const prohibitedOverlayTemplate = PROHIBITED_WIDGETS.includes(widget.Alias.toLowerCase())
			? html`
					<div class="pointer-events-none absolute inset-0">
						<!-- Line from top-left to bottom-right -->
						<div class="absolute left-0 top-0 h-full w-full">
							<svg class="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
								<line x1="0" y1="0" x2="100" y2="100" stroke="red" stroke-width="2" />
							</svg>
						</div>
						<!-- Line from top-right to bottom-left -->
						<div class="absolute left-0 top-0 h-full w-full">
							<svg class="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
								<line x1="100" y1="0" x2="0" y2="100" stroke="red" stroke-width="2" />
							</svg>
						</div>
					</div>
					<div class="text-center text-xs text-n-red">
						Not rendered by DGital because injects bootstrap
					</div>
			  `
			: "";

		return html`
		<div class="relative">
			<label
				class="relative mb-1 inline-block border-b border-solid border-n-blue bg-n-gray py-1.5 pl-3 pr-8 text-base"
				>${widget.Alias}
				<div class="absolute -right-1.5 -top-1.5 h-3 w-3 rotate-45 transform bg-white content-['']"></div
			></label>
			<ul class="list-none text-xs text-n-dark-gray">
				<li><span class="font-bold uppercase">Expires On:</span><br />${widget.ExpiresOn}</li>
				<li><span class="font-bold uppercase">Refreshed On:</span><br />${widget.RefreshedOn}</li>
			</ul>
			<div class="mx-0 mb-[25px] mt-[10px]">
				<ul>
					${widget.LocalizedWidgets.map(localizedWidgetTemplate)}
				</ul>
			</div>
			${prohibitedOverlayTemplate}
		</div>
	`;
	};

	const widgetsTemplate = () => html`
		<div class="mt-4 grid grid-cols-5 gap-4">${props.homeWidgetsValues.map(widgetTemplate)}</div>
	`;

	const cacheContentTemplate = () => !props.isPreview
		? html`<div class="flex w-full items-center justify-end gap-4">${stateTemplate()} ${buttonTemplate()}</div>${widgetsTemplate()}`
		: html`<div class="w-full"><label class="preview-label">In Preview</label></div>`;

	const htmlTemplate = () => html`
		<section class="mt-6">
			${cacheContentTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
